import {
  menu,
  type MenuConfig,
  popMenu,
  type PopupTarget,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { unsafeCSSVarV2 } from '@blocksuite/affine-shared/theme';
import {
  ArrowRightSmallIcon,
  DeleteIcon,
  DuplicateIcon,
  ExpandFullIcon,
  FilterIcon,
  GroupingIcon,
  InfoIcon,
  LayoutIcon,
  MoreHorizontalIcon,
  SortIcon,
} from '@blocksuite/icons/lit';
import { autoPlacement, offset, shift } from '@floating-ui/dom';
import { css, html } from 'lit';

import { popPropertiesSetting } from '../../../../core/common/properties.js';
import { filterTraitKey } from '../../../../core/filter/trait.js';
import {
  popGroupSetting,
  popSelectGroupByProperty,
} from '../../../../core/group-by/setting.js';
import {
  group2TraitKey,
  groupTraitKey,
} from '../../../../core/group-by/trait.js';
import {
  dataViewI18nT,
  type DataViewUILogicBase,
  emptyFilterGroup,
  popCreateFilter,
  renderUniLit,
} from '../../../../core/index.js';
import { popCreateSort } from '../../../../core/sort/add-sort.js';
import { sortTraitKey } from '../../../../core/sort/manager.js';
import { createSortUtils } from '../../../../core/sort/utils.js';
import { WidgetBase } from '../../../../core/widget/widget-base.js';
import { TableSingleView } from '../../../../view-presets/table/table-view-manager.js';
import { popFilterRoot } from '../../../quick-setting-bar/filter/root-panel-view.js';
import { popSortRoot } from '../../../quick-setting-bar/sort/root-panel.js';
import {
  closeOnViewIdRemoval,
  createViewPopupLifetime,
  viewIdExists,
} from '../../../view-existence.js';

const styles = css`
  .affine-database-toolbar-item.more-action {
    padding: 2px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  .affine-database-toolbar-item.more-action:hover {
    background: var(--streamlain-hover-color);
  }

  .affine-database-toolbar-item.more-action {
    font-size: 20px;
    color: ${unsafeCSSVarV2('icon/primary')};
  }

  .more-action.active {
    background: var(--streamlain-hover-color);
  }
`;

const dataViewMenuHoverColor =
  'color-mix(in srgb, var(--streamlain-primary-color) 10%, var(--streamlain-hover-color))';
const dataViewMenuEntryClass = { 'data-view-menu-entry': true } as const;
const dataViewNestedOptionsMenuClass = 'data-view-nested-options-menu';
const setDataViewMenuHoverColor = (handler: ReturnType<typeof popMenu>) => {
  handler.menu.menuElement.style.setProperty(
    '--affine-menu-hover-color',
    dataViewMenuHoverColor
  );
};

export class DataViewHeaderToolsViewOptions extends WidgetBase {
  static override styles = styles;

  clickMoreAction = (e: MouseEvent) => {
    e.stopPropagation();
    this.openMoreAction(popupTargetFromElement(e.currentTarget as HTMLElement));
  };

  openMoreAction = (target: PopupTarget) => {
    popViewOptions(target, this.dataViewLogic);
  };

  override render() {
    if (this.view.readonly$.value) {
      return;
    }
    return html` <div
      class="affine-database-toolbar-item more-action"
      @click="${this.clickMoreAction}"
    >
      ${MoreHorizontalIcon()}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'data-view-header-tools-view-options': DataViewHeaderToolsViewOptions;
  }
}
const createSettingMenus = (
  target: PopupTarget,
  dataViewLogic: DataViewUILogicBase,
  reopen: () => void,
  closeMenu: () => void,
  trackDrilldown: <T extends { close: () => void } | undefined>(handler: T) => T
) => {
  const view = dataViewLogic.view;
  const settingItems: MenuConfig[] = [];
  settingItems.push(
    menu.action({
      name: dataViewI18nT('viewSettings.properties'),
      prefix: InfoIcon(),
      class: dataViewMenuEntryClass,
      closeOnSelect: false,
      postfix: html` <div style="font-size: 14px;">
          ${dataViewI18nT('viewSettings.propertiesShown', {
            count: view.getBoundedPropertiesWindow().properties.length,
          })}
        </div>
        ${ArrowRightSmallIcon()}`,
      select: () => {
        trackDrilldown(
          popPropertiesSetting(
            target,
            {
              view: view,
              onBack: reopen,
              onClose: closeMenu,
            },
            [
              autoPlacement({
                allowedPlacements: ['bottom-start', 'top-start'],
              }),
              offset({ mainAxis: 15, crossAxis: -162 }),
              shift({ crossAxis: true }),
            ]
          )
        );
      },
    })
  );
  const filterTrait = view.traitGet(filterTraitKey);
  if (filterTrait) {
    const filterCount = filterTrait.filter$.value.conditions.length;
    settingItems.push(
      menu.action({
        name: dataViewI18nT('viewSettings.filter'),
        prefix: FilterIcon(),
        class: dataViewMenuEntryClass,
        closeOnSelect: false,
        postfix: html` <div style="font-size: 14px;">
            ${filterCount === 0
              ? ''
              : filterCount === 1
                ? dataViewI18nT('viewSettings.filterCountOne')
                : dataViewI18nT('viewSettings.filterCount', {
                    count: filterCount,
                  })}
          </div>
          ${ArrowRightSmallIcon()}`,
        select: () => {
          if (!filterTrait.filter$.value.conditions.length) {
            trackDrilldown(
              popCreateFilter(
                target,
                {
                  vars: view.vars$,
                  onBack: reopen,
                  onClose: closeMenu,
                  onSelect: filter => {
                    filterTrait.filterSet({
                      ...(filterTrait.filter$.value ?? emptyFilterGroup),
                      conditions: [
                        ...filterTrait.filter$.value.conditions,
                        filter,
                      ],
                    });
                    trackDrilldown(
                      popFilterRoot(
                        target,
                        {
                          filterTrait: filterTrait,
                          onBack: reopen,
                          onClose: closeMenu,
                          dataViewLogic: dataViewLogic,
                        },
                        [
                          autoPlacement({
                            allowedPlacements: ['bottom-start', 'top-start'],
                          }),
                          offset({ mainAxis: 15, crossAxis: -162 }),
                          shift({ crossAxis: true }),
                        ]
                      )
                    );
                    dataViewLogic.eventTrace('CreateDatabaseFilter', {});
                  },
                },
                {
                  middleware: [
                    autoPlacement({
                      allowedPlacements: ['bottom-start', 'top-start'],
                    }),
                    offset({ mainAxis: 15, crossAxis: -162 }),
                    shift({ crossAxis: true }),
                  ],
                }
              )
            );
          } else {
            trackDrilldown(
              popFilterRoot(
                target,
                {
                  filterTrait: filterTrait,
                  onBack: reopen,
                  onClose: closeMenu,
                  dataViewLogic: dataViewLogic,
                },
                [
                  autoPlacement({
                    allowedPlacements: ['bottom-start', 'top-start'],
                  }),
                  offset({ mainAxis: 15, crossAxis: -162 }),
                  shift({ crossAxis: true }),
                ]
              )
            );
          }
        },
      })
    );
  }
  const sortTrait = view.traitGet(sortTraitKey);
  if (sortTrait) {
    const sortCount = sortTrait.sortList$.value.length;
    settingItems.push(
      menu.action({
        name: dataViewI18nT('viewSettings.sort'),
        prefix: SortIcon(),
        class: dataViewMenuEntryClass,
        closeOnSelect: false,
        postfix: html` <div style="font-size: 14px;">
            ${sortCount === 0
              ? ''
              : sortCount === 1
                ? dataViewI18nT('viewSettings.sortCountOne')
                : dataViewI18nT('viewSettings.sortCount', {
                    count: sortCount,
                  })}
          </div>
          ${ArrowRightSmallIcon()}`,
        select: () => {
          const sortList = sortTrait.sortList$.value;
          const sortUtils = createSortUtils(
            sortTrait,
            dataViewLogic.eventTrace
          );
          if (!sortList.length) {
            trackDrilldown(
              popCreateSort(
                target,
                {
                  sortUtils: sortUtils,
                  onBack: reopen,
                  onClose: closeMenu,
                },
                {
                  middleware: [
                    autoPlacement({
                      allowedPlacements: ['bottom-start', 'top-start'],
                    }),
                    offset({ mainAxis: 15, crossAxis: -162 }),
                    shift({ crossAxis: true }),
                  ],
                }
              )
            );
          } else {
            trackDrilldown(
              popSortRoot(
                target,
                {
                  sortUtils: sortUtils,
                  title: {
                    text: dataViewI18nT('viewSettings.sort'),
                    onBack: reopen,
                    onClose: closeMenu,
                  },
                },
                [
                  autoPlacement({
                    allowedPlacements: ['bottom-start', 'top-start'],
                  }),
                  offset({ mainAxis: 15, crossAxis: -162 }),
                  shift({ crossAxis: true }),
                ]
              )
            );
          }
        },
      })
    );
  }
  const groupTrait = view.traitGet(groupTraitKey);
  if (groupTrait) {
    settingItems.push(
      menu.action({
        name: dataViewI18nT('viewSettings.group'),
        prefix: GroupingIcon(),
        class: dataViewMenuEntryClass,
        closeOnSelect: false,
        postfix: html` <div style="font-size: 14px;">
            ${groupTrait.property$.value?.name$.value ?? ''}
          </div>
          ${ArrowRightSmallIcon()}`,
        select: () => {
          const groupBy = groupTrait.property$.value;
          if (!groupBy) {
            trackDrilldown(
              popSelectGroupByProperty(
                target,
                groupTrait,
                {
                  onSelect: () =>
                    trackDrilldown(
                      popGroupSetting(target, groupTrait, reopen, closeMenu, [
                        autoPlacement({
                          allowedPlacements: ['bottom-start', 'top-start'],
                        }),
                        offset({ mainAxis: 15, crossAxis: -162 }),
                        shift({ crossAxis: true }),
                      ])
                    ),
                  onBack: reopen,
                  onClose: closeMenu,
                },
                [
                  autoPlacement({
                    allowedPlacements: ['bottom-start', 'top-start'],
                  }),
                  offset({ mainAxis: 15, crossAxis: -162 }),
                  shift({ crossAxis: true }),
                ]
              )
            );
          } else {
            trackDrilldown(
              popGroupSetting(target, groupTrait, reopen, closeMenu, [
                autoPlacement({
                  allowedPlacements: ['bottom-start', 'top-start'],
                }),
                offset({ mainAxis: 15, crossAxis: -162 }),
                shift({ crossAxis: true }),
              ])
            );
          }
        },
      })
    );

    // Sub group — only for views that have a group2 trait (Kanban).
    // Only shown after a primary group property is selected.
    const group2Trait = view.traitGet(group2TraitKey);
    if (group2Trait && groupTrait.property$.value) {
      const subGroupLabel = group2Trait.property$.value?.name$.value ?? 'None';
      const middleware2 = [
        autoPlacement({ allowedPlacements: ['bottom-start', 'top-start'] }),
        offset({ mainAxis: 15, crossAxis: -162 }),
        shift({ crossAxis: true }),
      ];
      settingItems.push(
        menu.action({
          name: dataViewI18nT('viewSettings.subGroup'),
          prefix: GroupingIcon(),
          class: dataViewMenuEntryClass,
          closeOnSelect: false,
          postfix: html`<div style="font-size: 14px;">${subGroupLabel}</div>
            ${ArrowRightSmallIcon()}`,
          select: () => {
            const subGroup2 = group2Trait.property$.value;
            if (!subGroup2) {
              trackDrilldown(
                popSelectGroupByProperty(
                  target,
                  group2Trait,
                  {
                    onSelect: () =>
                      trackDrilldown(
                        popGroupSetting(
                          target,
                          group2Trait,
                          reopen,
                          closeMenu,
                          middleware2
                        )
                      ),
                    onBack: reopen,
                    onClose: closeMenu,
                  },
                  middleware2
                )
              );
            } else {
              trackDrilldown(
                popGroupSetting(
                  target,
                  group2Trait,
                  reopen,
                  closeMenu,
                  middleware2
                )
              );
            }
          },
        })
      );
    }
  }

  return settingItems;
};

const createLayoutMenuItems = (
  dataViewLogic: DataViewUILogicBase,
  closeMenu: () => void
) => {
  const view = dataViewLogic.view;
  return view.manager.viewMetas.map(meta =>
    menu.action({
      class: dataViewMenuEntryClass,
      name:
        meta.type === 'table'
          ? dataViewI18nT('table.defaultName')
          : meta.model.defaultName,
      prefix: renderUniLit(meta.renderer.icon),
      isSelected: meta.type === view.manager.currentView$.value?.type,
      select: () => {
        const id = view.manager.currentViewId$.value;
        if (!id || meta.type === view.manager.currentView$.value?.type) {
          return;
        }
        view.manager.viewChangeType(id, meta.type);
        dataViewLogic.clearSelection();
        closeMenu();
      },
    })
  );
};

const getLocalizedLayoutType = (type: string): string => {
  if (type === 'gantt') {
    return dataViewI18nT('viewSettings.layout.gantt');
  }
  if (type === 'kanban') {
    return dataViewI18nT('viewSettings.layout.kanban');
  }
  if (type === 'table') {
    return dataViewI18nT('viewSettings.layout.table');
  }
  return type;
};

const popViewOptionsInLifetime = (
  target: PopupTarget,
  dataViewLogic: DataViewUILogicBase
) => {
  const view = dataViewLogic.view;
  if (!viewIdExists(view.manager.views$, view.id)) {
    return;
  }
  const trackDrilldown = <T extends { close: () => void } | undefined>(
    drilldown: T
  ): T => drilldown;
  const reopen = () => {
    popViewOptionsInLifetime(target, dataViewLogic);
  };
  const items: MenuConfig[] = [];
  items.push(
    menu.input({
      initialValue: view.name$.value,
      placeholder: dataViewI18nT('view.namePlaceholder'),
      onChange: text => {
        view.nameSet(text);
      },
    })
  );
  const layoutAndDisplayItems: MenuConfig[] = [
    menu.subMenu({
      name: dataViewI18nT('viewSettings.layout'),
      class: 'data-view-menu-entry',
      postfix: html` <div style="font-size: 14px;text-transform: capitalize;">
        ${getLocalizedLayoutType(view.type)}
      </div>`,
      options: {
        className: dataViewNestedOptionsMenuClass,
        title: {
          onBack: reopen,
          text: dataViewI18nT('viewSettings.layout'),
        },
        items: createLayoutMenuItems(dataViewLogic, () => handler.close()),
      },
      middleware: [
        autoPlacement({
          allowedPlacements: ['bottom-end', 'top-end'],
        }),
        offset(4),
        shift({ crossAxis: true }),
      ],
      openOnHover: false,
      prefix: LayoutIcon(),
    }),
  ];

  // Streamlain Extension: Cell Display Mode for Table Views (after Layout)
  if (view instanceof TableSingleView) {
    const currentMode = view.cellDisplayMode$.value;
    const modeLabels: Record<'collapsed' | 'expanded' | 'smart', string> = {
      collapsed: dataViewI18nT('viewSettings.cellDisplay.collapsed'),
      expanded: dataViewI18nT('viewSettings.cellDisplay.expanded'),
      smart: dataViewI18nT('viewSettings.cellDisplay.smart'),
    };
    layoutAndDisplayItems.push(
      menu.subMenu({
        name: dataViewI18nT('viewSettings.cellDisplay'),
        prefix: ExpandFullIcon(),
        class: 'data-view-menu-entry',
        postfix: html`<div style="font-size: 14px;">
          ${modeLabels[currentMode]}
        </div>`,
        options: {
          className: dataViewNestedOptionsMenuClass,
          title: {
            text: dataViewI18nT('viewSettings.cellDisplay'),
            onBack: reopen,
          },
          items: [
            menu.action({
              name: dataViewI18nT('viewSettings.cellDisplay.expanded'),
              class: dataViewMenuEntryClass,
              isSelected: currentMode === 'expanded',
              select: () => {
                view.cellDisplayModeSet('expanded');
                reopen();
                handler.close();
              },
            }),
            menu.action({
              name: dataViewI18nT('viewSettings.cellDisplay.collapsed'),
              class: dataViewMenuEntryClass,
              isSelected: currentMode === 'collapsed',
              select: () => {
                view.cellDisplayModeSet('collapsed');
                reopen();
                handler.close();
              },
            }),
            menu.action({
              name: dataViewI18nT('viewSettings.cellDisplay.smart'),
              class: dataViewMenuEntryClass,
              isSelected: currentMode === 'smart',
              select: () => {
                view.cellDisplayModeSet('smart');
                reopen();
                handler.close();
              },
            }),
          ],
        },
      })
    );
  }

  items.push(menu.group({ items: layoutAndDisplayItems }));

  items.push(
    menu.group({
      items: createSettingMenus(
        target,
        dataViewLogic,
        reopen,
        () => handler.close(),
        trackDrilldown
      ),
    })
  );
  items.push(
    menu.group({
      items: [
        menu.action({
          name: dataViewI18nT('view.duplicate'),
          prefix: DuplicateIcon(),
          class: dataViewMenuEntryClass,
          closeOnSelect: false,
          select: () => {
            view.duplicate();
          },
        }),
        menu.action({
          name: dataViewI18nT('view.delete'),
          prefix: DeleteIcon(),
          closeOnSelect: false,
          hide: () => view.manager.views$.value.length <= 1,
          select: () => {
            view.delete();
          },
          class: { 'delete-item': true, ...dataViewMenuEntryClass },
        }),
      ],
    })
  );
  let handler: ReturnType<typeof popMenu>;
  handler = popMenu(target, {
    options: {
      title: {
        text: dataViewI18nT('viewSettings.title'),
        onClose: () => handler.close(),
      },
      items,
      onClose: () => handler?.menu.closeSubMenu(),
    },
    middleware: [
      autoPlacement({ allowedPlacements: ['bottom-start'] }),
      offset({ mainAxis: 15, crossAxis: -162 }),
      shift({ crossAxis: true }),
    ],
  });
  setDataViewMenuHoverColor(handler);
  return handler;
};

export const popViewOptions = (
  target: PopupTarget,
  dataViewLogic: DataViewUILogicBase,
  onClose?: () => void
) => {
  const view = dataViewLogic.view;
  if (!viewIdExists(view.manager.views$, view.id)) {
    return;
  }
  let disposeViewRemoval = () => {};
  const lifetime = createViewPopupLifetime(() => {
    disposeViewRemoval();
    onClose?.();
  });
  const scopedTarget: PopupTarget = {
    ...target,
    popupLifetime: lifetime,
  };
  const handler = popViewOptionsInLifetime(scopedTarget, dataViewLogic);
  disposeViewRemoval = closeOnViewIdRemoval(
    view.manager.views$,
    view.id,
    lifetime.close
  );
  return handler;
};
